/**
 * Error Boundary Component
 * Catches JavaScript errors in child components and displays fallback UI.
 * @packageDocumentation
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ChevronUp, RefreshCw, AlertCircle, ChevronDown } from 'lucide-react';

import { Button } from './button';
import { Card, CardContent } from './card';

/**
 * Props for the ErrorBoundary component.
 */
interface ErrorBoundaryProps {
  /** URL for reporting issues. */
  reportUrl?: string;
  /** Child components to render. */
  children: ReactNode;
  /** Optional custom fallback UI. */
  fallback?: ReactNode;
  /** Whether to show a report issue link. */
  showReportLink?: boolean;
  /** Callback fired when an error is caught. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

/**
 * State for the ErrorBoundary component.
 */
interface ErrorBoundaryState {
  /** Whether an error has been caught. */
  hasError: boolean;
  /** The caught error instance. */
  error: Error | null;
  /** Whether the error details section is expanded. */
  showDetails: boolean;
}

/**
 * Error boundary component that catches and handles React errors.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  /**
   * Creates an ErrorBoundary instance.
   *
   * @param { ErrorBoundaryProps } props Component props.
   */
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
      hasError: false,
      showDetails: false,
    };
  }

  /**
   * Derives state from caught error.
   *
   * @param { Error } error The caught error.
   * @returns { ErrorBoundaryState } Updated state with error.
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      error,
      hasError: true,
    };
  }

  /**
   * Handles errors caught by the boundary.
   *
   * @param { Error } error The caught error.
   * @param { ErrorInfo } errorInfo React error information.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';

    if (isDev) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    this.props.onError?.(error, errorInfo);
  }

  /**
   * Resets the error state to retry rendering.
   */
  handleReset = (): void => {
    this.setState({
      error: null,
      hasError: false,
      showDetails: false,
    });
  };

  /**
   * Toggles the visibility of error details.
   */
  handleToggleDetails = (): void => {
    this.setState((prevState) => ({
      showDetails: !prevState.showDetails,
    }));
  };

  /**
   * Renders the component or error fallback.
   *
   * @returns { ReactNode } The rendered content.
   */
  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="size-6 text-destructive" />
              </div>

              <h2 className="mb-2 text-center text-xl font-semibold text-foreground">Something went wrong</h2>

              <p className="mb-4 text-center text-sm text-muted-foreground">
                An unexpected error occurred in the application.
              </p>

              <button
                type="button"
                onClick={this.handleToggleDetails}
                className="mb-4 flex w-full items-center justify-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                {this.state.showDetails ? (
                  <>
                    <ChevronUp className="size-4" />
                    Hide details
                  </>
                ) : (
                  <>
                    <ChevronDown className="size-4" />
                    Show details
                  </>
                )}
              </button>

              {this.state.showDetails && (
                <div className="mb-4 rounded-md border border-border bg-muted/50 p-3">
                  <p className="mb-2 text-xs font-medium text-foreground">{this.state.error?.name || 'Error'}</p>
                  <p className="mb-2 text-xs text-muted-foreground">{this.state.error?.message || 'Unknown error'}</p>
                  {this.state.error?.stack && (
                    <pre className="max-h-32 overflow-auto text-xs text-muted-foreground/80">
                      {this.state.error.stack}
                    </pre>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <Button className="w-full" onClick={this.handleReset} leftIcon={<RefreshCw />}>
                  Try again
                </Button>

                {this.props.showReportLink && (
                  <a
                    href={this.props.reportUrl || '/support'}
                    className="block text-center text-sm text-primary transition-colors hover:text-primary/80"
                  >
                    Report this issue
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
